import { appleCheckLogin, companyInviteLogin, getActivity, getEmailCaptcha, getSmsCaptcha, getTokenByCode, googleLoginCallback, reportLogin, ssoLoginCallback, userLogin } from '@/api/user/user'
import gtagTools from '@/libs/gtag-tools'
import { openNewWindow, testValue } from '@/libs/tools'
import { getSessionStorage, isInMacWebview, isInWebview, postMessageToOpenerWindow, pushDebugInfo, redirectApp, setSessionStorage, setToken } from '@/libs/utils'
import i18n from '@/locale'
import $router from '@/router'
import Base64 from 'base-64'
import { ElMessage } from 'element-plus'
import { cloneDeep } from 'lodash'
import { ref } from 'vue'

import { redirectToAccountCenter, redirectToLogin, redirectToCompanyAdmin } from './base'
import { appStore, userStore } from '@/stores'
const { t } = i18n.global
const isCN = ref(appStore.isCN)
/**
 * 设置缓存的登陆表单
 * @param {String} formData 表单数据
 */
export const setLoginFormCache = formData => {
  if (formData) {
    Object.assign(userStore.loginFormData, formData)
    console.log(formData, userStore.loginFormData)
    setSessionStorage('loginFormData', JSON.stringify(formData))
  } else {
    userStore.loginFormData.username = ''
    userStore.loginFormData.codeId = ''
    userStore.loginFormData.code = ''
    setSessionStorage('loginFormData', '')
  }
}

/**
 * 获取缓存的登陆表单
 * @returns {username: '', codeId: '', code: ''}
 */
export const getLoginFormCache = () => {
  if (hasUsername()) {
    return userStore.loginFormData
  } else {
    const cacheFormData = getSessionStorage('loginFormData')
    if (cacheFormData) {
      const formData = JSON.parse(cacheFormData)
      Object.assign(userStore.loginFormData, formData)
      return formData
    }
  }
  return { username: '' }
}

/**
 * 是否有填写账号，用于刷新二级页面时判断
 * @returns
 */
export const hasUsername = () => {
  return userStore.loginFormData.username !== ''
}

/**
 * 是否有发送验证码
 * @returns
 */
export const hasSentCode = () => {
  return hasUsername() && userStore.loginFormData.codeId !== ''
}

/**
 * 是否已输入验证码
 * @returns
 */
export const hasInputCode = () => {
  return hasSentCode() && userStore.loginFormData.code !== ''
}

/**
 * 缓存验证码ID，用于10分钟内发送相同的验证码
 * @param {*} account 账号
 * @param {*} type 验证码场景类型
 * @param {*} codeId 验证码ID
 */
export const setCaptchaCache = (account, type, codeId) => {
  const cacheKey = `captcha_${type}_${account}`
  const cacheData = {
    codeId,
    time: new Date().getTime(),
  }
  setSessionStorage(cacheKey, JSON.stringify(cacheData))
}

/**
 * 获取缓存的验证码ID
 * @param {*} account 账号
 * @param {*} type 场景类型
 * @returns {String} codeId
 */
export const getCaptchaCache = (account, type) => {
  let cacheCodeId = ''
  const cacheKey = `captcha_${type}_${account}`
  let cacheData = getSessionStorage(cacheKey)
  const cacheMinutes = 10 // 10分钟内重发验证码要把上次的codeId带过去
  if (cacheData) {
    cacheData = JSON.parse(cacheData)
    if (cacheData.time && cacheData.time + 60 * cacheMinutes * 1000 > new Date().getTime()) {
      cacheCodeId = cacheData.codeId
    }
  }
  return cacheCodeId
}

/**
 * 移除缓存的验证码ID
 * @param {*} account 账号
 * @param {*} type 场景类型
 */
export const removeCaptchaCache = (account, type) => {
  const cacheKey = `captcha_${type}_${account}`
  setSessionStorage(cacheKey)
}

/**
 * 发送邮箱或手机验证码(10分钟内发送同一个codeId)
 * 注意：发送完验证码，验证完成以后需要调用 removeCaptchaCache() 方法清除缓存，否则下次还是会使用上次的验证码id
 * @param {String} account
 * @param {Number} type 验证码类型，见emums里的定义
 * @returns
 */
export const sendCaptcha = async (account, type, isResend = false) => {
  const cacheCodeId = getCaptchaCache(account, type)
  // 判断是邮箱还是手机，不同方式接口和参数不一样
  const accountType = testValue('phone', account) ? 'phone' : 'email'
  const getCaptchaApi = accountType === 'phone' ? getSmsCaptcha : getEmailCaptcha
  const requestBody =
    accountType === 'phone'
      ? {
          mobile: parseInt(account),
          type,
        }
      : {
          email: account,
          type,
        }
  if (cacheCodeId) requestBody.codeId = cacheCodeId
  if (isResend) requestBody.isResend = isResend
  const { data } = await getCaptchaApi(requestBody)
  // 如果获取到的验证码和缓存的不一致，则重新缓存新的codeId
  if (data.codeId !== cacheCodeId) {
    setCaptchaCache(account, type, data.codeId)
  }
  return {
    account,
    codeId: data.codeId,
    accountType,
  }
}

/**
 * 缓存登陆账号信息
 * @param {*} account 邮箱或手机号或账号
 * @param {*} accountType 账号类型：email，phone，account
 */
export const setLoginAccountDataToSession = (account, accountType) => {
  const loginData = {
    account,
    accountType,
  }
  setSessionStorage('loginAccountData', JSON.stringify(loginData))
}

/**
 * 获取登陆账号信息
 * @returns {Object} {account, accountType}
 */
export const getLoginAccountDataFormSession = () => {
  const res = getSessionStorage('loginAccountData')
  if (res) {
    return JSON.parse(res)
  }
  return null
}

/**
 * 设置登录密码到临时缓存(base64加密)
 * 用于密码登录时同一个账号下多个相同密码账号选择登录时获取账号列表接口使用
 * @param {String} loginPass 登录密码
 */
export const setLoginPassToSession = loginPass => {
  loginPass = loginPass ? Base64.encode(encodeURI(loginPass)) : ''
  setSessionStorage('loginPass', loginPass)
}

/**
 * 获取登录时临时缓存的密码(base64解密后的)
 * @returns {String} 登录密码
 */
export const getLoginPassFromSession = () => {
  const loginPass = getSessionStorage('loginPass')
  return loginPass || ''
}

/**
 * 解析url地址中的tokenData
 * @param {*} tokenData
 * @returns base64 后的token数据{token: xxxxx, expired: xxxxx}
 */
export const decodeTokenData = tokenData => {
  return JSON.parse(atob(tokenData))
}

/**
 * 退出账号
 * @param {Boolean} autoLoginCallback 再次登录成功后是否返回刚才的页面
 * @param {Boolean} isCompanyManager 是否跳转到管理员登录页
 * @returns
 */
export const logOutSuccess = (autoLoginCallback = true, isCompanyManager = false) => {
  setToken('', 0)
  // 如果是iOS或者安卓需要通知app同步退出
  if (appStore.isiOS || appStore.isAndroid || appStore.isIframe) {
    redirectApp('logOut')
    return
  }
  setTimeout(() => {
    redirectToLogin(autoLoginCallback ? location.href : '', isCompanyManager)
  }, 500)
}

/**
 * 储存或清空临时的登录结果信息，用于刷新页面时保证数据不丢失等情况
 * @param {*} loginResult 
 */
export const setLoginResultTmp = (loginResult) => {
  if (loginResult) {
    setSessionStorage('loginResult', JSON.stringify(loginResult))
  } else {
    setSessionStorage('loginResult', '')
  }
}

/**
 * 获取临时储存的登录结果信息，用于调用loginSuccess方法时使用
 * @returns 
 */
export const getLoginResultTmp = () => {
  const loginResult = getSessionStorage('loginResult')
  if (loginResult) {
    return JSON.parse(loginResult)
  }
  return null
}

/**
 * 第三方登录成功回调
 * @param {String} loginType 登录类型：google|apple
 * @param {Object} query     登录成功后地址栏参数
 * @param {String} loginFrom 登录来源
 *                     pc：pc端登录：从内嵌页跳转到浏览器
 *                     web: web端登录：打开新的浏览器弹窗
 * @returns
 */
export const thirdPartyAuthCallback = async (loginType, query, loginFrom) => {
  let loginResult = null
  const isInIframe = loginFrom === 'web'
  try {
    if (loginType === 'google') {
      // 谷歌登录
      const { data } = await googleLoginCallback(query)
      loginResult = data
      if (isInIframe) {
        // 网站登录情况(浏览器新弹窗打开)
        if (query.state === 'updfstate') {
          // 谷歌登录(通知父窗口用户信息)
          postMessageToOpenerWindow('googleLoginSuccess', { loginResult })
        } else {
          // 绑定账号(跳转到绑定回调页进行处理)
          $router.replace({
            name: 'bindAccountCallback',
            params: {
              bindType: loginType,
            },
            query: {
              status: data.status,
            },
          })
          return ''
        }
      }
    } else if (loginType === 'apple') {
      const { data } = await appleCheckLogin(query.code)
      loginResult = data
      if (isInIframe) {
        if (query.state === 'updfstate') {
          // 网站登录情况(浏览器新弹窗打开)
          postMessageToOpenerWindow('appleLoginSuccess', { loginResult })
        } else {
          // 绑定账号(跳转到绑定回调页进行处理)
          $router.replace({
            name: 'bindAccountCallback',
            params: {
              bindType: loginType,
            },
            query: {
              status: data.status,
            },
          })
          return ''
        }
      }
    } else if (loginType === 'sso') {
      try {
        const reqId = query.reqId
        const { data } = await ssoLoginCallback(reqId)
        loginResult = data
        if (isInIframe) {
          postMessageToOpenerWindow('ssoLoginSuccess', { loginResult })
        }
      } catch (e) {
        console.log(e)
      }
    }
    // 谷歌数据上报
    loginResult.isNew ? gtagTools.signUp(loginType) : gtagTools.signIn(loginType)
    return loginResult
  } catch (e) {
    console.log(e)
    if (isInIframe) {
      setTimeout(() => {
        if (!getSessionStorage('disabledAutoCloseNewWindow')) window.close()
      }, 2000)
    }
    return false
  }
}

/**
 * 账号+密码/验证码登录
 * @param {*} loginType 登录类型：email_code, phone_code, email_password, phone_password
 * @param {*} loginFormData 登录表单
 * @param {*} action 操作类型：默认 => 正常登录流程，joinCompany => 加入企业(跳转到加入企业成功)
 * @returns Promise<Object>
 */
export const accountLogin = async (loginType, loginFormData, action = '') => {
  const isJoinCompany = action === 'joinCompany'
  try {
    let loginResult = null
    if (isJoinCompany) {
      loginFormData.inviteCode = userStore.inviteCode || $router.currentRoute.value.query?.inviteCode
      const res = await companyInviteLogin(loginFormData)
      loginResult = res.data
    } else {
      const res = await userLogin(loginFormData)
      loginResult = res.data
    }
    // 如果是新用户，则先保存token，在跳转到完善信息页
    if (loginResult.isNew) {
      setLoginResultTmp(loginResult)
      setToken(loginResult.token, loginResult.expiresAt)
      $router.push({
        name: 'loginInfoUpdata',
        query: {
          ...$router.currentRoute.value.query,
        },
      })
    } else {
      const res = await loginSuccess(loginType, loginResult, action)
      if (!res) throw new Error(res?.msg || res.message)
    }
    return loginResult
  } catch (err) {
    throw new Error(err?.msg || err.message)
  }
}

/**
 * 登录或注册成功
 * 检测已登录账号下是否有绑定多个账号，如果有则跳转到账号选择页
 * @param {String} loginType 登录类型
 *  account_password // 企业账号登录
    email_code // 邮箱验证码
    email_password // 邮箱密码
    phone_code // 手机验证码
    phone_password // 手机验证码
    apple // 苹果账号
    google // 谷歌账号
    wechat // 微信账号
    sso // sso
    google_one_tap // 谷歌一键登录
    quick_login // 快捷注册
 * @param {*} loginResult 登录结果，包含token和用户信息等
 * @param {*} action 操作类型：默认 => 正常登录流程，joinCompany => 加入企业(跳转到加入企业成功)，companyLogin => 企业管理员登录
 */
export const loginSuccess = async (loginType, loginResult, action = '') => {
  const isCN = appStore.isCN
  // 临时储存登录成功信息，防止刷新页面导致的登录成功信息丢失
  if (loginResult) {
    setLoginResultTmp(loginResult)
  } else {
    loginResult = getLoginResultTmp()
  }
  const isJoinCompany = action === 'joinCompany'
  const isCompanyLogin = action === 'companyLogin'
  const { user, isNew, token, expiresAt, hasMoreAccount, notGotoSelectAccount } = loginResult
  if (!user.country) {
    user.country = userStore.country
  }
  isNew ? gtagTools.signUp(loginType) : gtagTools.signIn(loginType)
  const query = cloneDeep($router.currentRoute.value.query)
  // 保存token
  setToken(token, expiresAt)
  // 上报登录数据
  try {
    await reportLoginData(loginType)
  } catch (err) {
    err.msg = '数据上报失败'
    pushDebugInfo(err)
  }
  // 加入企业成功
  if (isJoinCompany) {
    $router.replace({
      name: 'loginJoinSuccess',
      query: {
        loginType,
        ...query,
      },
    })
    // 企业账号(非sso登录的)或者账号下有多个子账号的跳转到选择账号页
  } else if (((user.companyUser && !user.companyInfo.isSSO) || hasMoreAccount) && !notGotoSelectAccount) {
    const routeName = $router.currentRoute.value.name === 'loginManager' ? 'loginManagerSelectAccount' : 'loginSelectAccount'
    // 多个账号下选择登录账号
    $router.replace({
      name: routeName,
      query: {
        loginType,
        ...query,
      },
    })
    return true
  } else if (isCompanyLogin) {
    redirectToCompanyAdmin()
  } else {
    // 判断账号是否在客户端已登录
    const loggedAccounts = userStore.loggedAccounts
    if (loggedAccounts.includes(loginResult.user.uid)) {
      ElMessage.error(t('This account is already logged in'))
      return false
    }
    // 如果是第三方新账号注册，需要跳转到问题收集页
    if (isNew && ['apple', 'google', 'wechat'].includes(loginType)) {
      $router.push({
        name: 'loginInfoReport',
        query,
      })
    } else {
      // 没有多个账号下
      redirectToLoginSuccess(loginType, loginResult)
    }
  }
  return true
}

/**
 * 登录成功跳转到成功提示页
 * @param {*} loginType 登录类型
 * @param {*} loginResult 用户信息
 * @param {Boolean} needGaEvent 是否需要上报GA
 */
export const redirectToLoginSuccess = (loginType, loginResult) => {
  $router.replace({
    name: 'loginCallback',
    params: {
      loginResult: JSON.stringify(loginResult),
      loginType,
    },
    query: {
      type: loginResult.isNew ? 'sign-up' : 'sign-in',
      ...$router.currentRoute.value.query,
    },
  })
}

/**
 * 通知app或网页父窗口登录信息，如果登录页面是在网页单独打开，则直接跳转到个人中心
 * @param {*} loginType 登录类型
 * @param {*} loginResult 登录结果信息
 * @param {Boolean} openApp 是否是指定打开客户端
 * @returns
 */
export const postLoginResultToApp = async (loginType, loginResult, openApp = false) => {
  // 刷新页面时候会导致登录结果信息丢失，可冲sessionStorege里重新读取
  loginResult = loginResult || getLoginResultTmp()
  // 如果是直接浏览器打开的登录，则成功后直接进入用户中心(个人账号统一跳转用户中心，其他情况根据redirectUri跳转)或企业管理后台
  if (!appStore.isIframe && !appStore.schemeName && !openApp) {
    setTimeout(() => {
      const redirectUri = $router.currentRoute.value.query?.redirectUri
      if (loginResult?.user?.role === 0) {
        redirectToAccountCenter()
      } else if (redirectUri) {
        location.href = decodeURIComponent(redirectUri)
      } else {
        redirectToAccountCenter()
      }
    }, 1000)
    return
  }
  const { user, isNew, expiresAt, token } = loginResult
  const data = {
    userInfo: user,
    token,
    isNewUser: isNew,
    signType: loginType,
  }
  // 后台返回expiresAt统一是毫秒，网页端使用tokenExpired(毫秒)，其他客户端使用expiresAt(秒)
  if (appStore.isIframe) {
    data.tokenExpired = expiresAt
  } else {
    data.expiresAt = parseInt(expiresAt / 1000)
  }
  // 谷歌一键登录指定使用通知父窗口方式
  const redirectType = loginType === 'google_one_tap' ? 'postMessage' : 'default'
  // 通知父窗口或PC端登录成功用户信息
  redirectApp(
    'loginSuccess',
    {
      data,
    },
    redirectType
  )
  // 网页端登录或webview内嵌页登录的检测活动（只针对非企业用户）
  if ((appStore.isIframe || isInWebview()) && !openApp) {
    let hasActivity = false
    if (!loginResult?.user?.companyUser) {
      hasActivity = await checkLoginSuccessActivity()
    }
    console.log('hasActivity', hasActivity)
    if (!hasActivity) {
      // 没有活动则通知关闭窗口
      setTimeout(() => {
        loginClose()
      }, 1000)
    }
  }
}

/**
 * 关闭登录窗口，包含网页端右侧划出的弹窗或客户端弹窗
 */
export const loginClose = () => {
  if (isInWebview()) {
    redirectApp('closeWindow')
  } else {
    redirectApp('loginClose')
  }
}

/**
 * 登录成功查询是否有活动，有的话直接跳转到活动页
 * @returns Promise<{Boolean}>
 */
export const checkLoginSuccessActivity = async () => {
  const isCN = appStore.isCN
  const isLoginFromCheckout = appStore.isLoginFromCheckout
  // 如果是收银台登录，则不跳转活动页
  if (isLoginFromCheckout) return false
  // 如果时直接打开的登录页，则不跳转活动页
  if (window.top === window) return false
  // 内嵌页不跳转活动
  if (isInMacWebview()) return false
  try {
    const activity = await getActivity('webLoginSuccess' + (isCN ? 'CN' : 'US'))
    if (activity.code === 200 && activity.data.activityId) {
      $router.replace({
        name: 'activityLoginSuccess',
      })
      return true
    }
  } catch (e) {
    console.log(e)
  }
  return false
}

/**
 * 登录成功后上报数据
 * @param {string} loginType 同loginSuccess 方法的loginType参数
 * @returns
 */
export const reportLoginData = async (loginType) => {
  const data = {
    loginType,
    language: appStore.language,
    deviceType: appStore.deviceType,
    productName: appStore.productName,
    country: userStore.country,
  }
  return await reportLogin(data)
}

/**
 * 通过code更新token
 * @param {*} code
 */
export const resetTokenByCode = async code => {
  const { data } = await getTokenByCode(code)
  setToken(data.token, data.expiresAt)
}

/**
 * 不同类型链接跳转根据访问终端来：
 * 普通链接：如隐私协议，MAC，WIN内嵌页需要打开浏览器并跳转，WEB直接跳转
 * 苹果登录：MAC直接跳转，WIN内嵌页打开浏览器，WEB新弹出窗口打开
 * 谷歌登录：MAC、WIN内嵌页打开浏览器跳转，WEB新弹出窗口打开
 * @param {*} type href => 普通链接，appleLogin => 苹果登录，googleLogin => 谷歌登录，ssoLogin => sso登录
 * @param {*} url 跳转的地址
 * @param {String} newWindowName 新窗口名称
 */
export const redirectUrl = (type, url, newWindowName = 'accountNewWindow') => {
  switch (type) {
    case 'href':
      if (isInWebview()) {
        redirectApp('openBrowser', { url })
      } else {
        window.open(url, '_blank')
      }
      break
    case 'ssoLogin':
      if (isInWebview()) {
        redirectApp('openBrowser', { url })
      } else {
        openNewWindow(url, newWindowName)
      }
      break
    case 'appleLogin':
      if (isInMacWebview()) {
        // mac内嵌页直接跳转，其他内嵌页打开浏览器
        location.href = url
      } else if (isInWebview()) {
        redirectApp('openBrowser', { url })
      } else {
        openNewWindow(url, newWindowName)
      }
      break
    case 'googleLogin':
      if (isInWebview()) {
        redirectApp('openBrowser', { url })
      } else {
        openNewWindow(url, newWindowName)
      }
      break
    default:
      window.open(url, '_blank')
  }
}
